/* eslint-disable no-param-reassign */
// Reference writes update the grid's live cell objects and the cached store securities in place, on purpose.
import { partition } from 'lodash';
import type { SecurityCitationCreateResult, SecurityMappingCreateResult } from 'services/captable';
import type { CitationReferenceData, MappingReferenceData } from 'services/reference-generator/types';
import convertCamelCaseToSnakeCase from 'utilities/convertCamelCaseToSnakeCase/convertCamelCaseToSnakeCase';
import { withoutIds } from './clearEvidence';
import { isEditableMappingReference, type MappingFlagged } from './mappingReferenceStatus';
import { citationReviewStatus } from './reviewAggregator';
import { syncReferenceGreen } from './securityAiPatches';
import { applyCitationReviewToRefs } from './settleCitationReview';

/** The cell fields a reference write touches. */
type ReferenceCell = {
  citationReferences?: CitationReferenceData[];
  mappingReferences?: MappingReferenceData[];
  retiredMappingIds?: number[];
  className?: string | null;
  dispatchEvent?: () => void;
};

/** The cached store security fields a reference write touches. */
export type ReferenceSecurity = {
  id?: number;
  citation_references?: CitationReferenceData[];
  mapping_references?: MappingReferenceData[];
};

/** True for a security the server already stores; its references save through the create calls, not Save. */
export const isSavedSecurityId = (id: unknown): boolean => Number(id) > 0;

/**
 * The citations once the server's row is in: it replaces its id twin or joins the end, and the rows
 * a Replace dismissed read as dismissed.
 */
export const placeCreatedCitation = (
  refs: CitationReferenceData[] | undefined,
  { citation, dismissed_ids: dismissedIds }: SecurityCitationCreateResult
): CitationReferenceData[] => {
  const list = refs ?? [];
  const placed = list.some(ref => ref.id === citation.id)
    ? list.map(ref => (ref.id === citation.id ? citation : ref))
    : [...list, citation];
  return dismissedIds.reduce((next, id) => applyCitationReviewToRefs(next, id, 'dismiss') ?? next, placed);
};

/**
 * The Excel references once the server's pick is in: the rows it retired go, and it replaces its id twin
 * or leads the list as the cell's reference.
 */
export const placeCreatedMapping = (
  refs: MappingReferenceData[] | undefined,
  { mapping, retired_ids: retiredIds }: SecurityMappingCreateResult
): MappingReferenceData[] => {
  const kept: MappingReferenceData[] = withoutIds(refs, new Set(retiredIds)) ?? [];
  return kept.some(ref => ref.id === mapping.id)
    ? kept.map(ref => (ref.id === mapping.id ? mapping : ref))
    : [mapping, ...kept];
};

/** Writes a saved citation onto the cell and, in place, onto its store security, so a grid rebuild keeps it. */
export const writeCreatedCitation = (
  cell: ReferenceCell | undefined,
  security: ReferenceSecurity | undefined,
  result: SecurityCitationCreateResult
): void => {
  if (cell) {
    cell.citationReferences = placeCreatedCitation(cell.citationReferences, result);
    cell.dispatchEvent?.();
  }
  if (security) {
    security.citation_references = placeCreatedCitation(security.citation_references, result);
  }
};

/**
 * Writes a saved Excel pick onto the cell (green, and off the next Save's retire list) and, in place,
 * onto its store security, so a grid rebuild keeps it.
 */
export const writeCreatedMapping = (
  cell: ReferenceCell | undefined,
  security: ReferenceSecurity | undefined,
  result: SecurityMappingCreateResult
): void => {
  if (cell) {
    cell.mappingReferences = placeCreatedMapping(cell.mappingReferences, result);
    cell.retiredMappingIds = cell.retiredMappingIds?.filter(id => id !== result.mapping.id);
    syncReferenceGreen(cell);
    cell.dispatchEvent?.();
  }
  if (security) {
    security.mapping_references = placeCreatedMapping(security.mapping_references, result);
  }
};

/**
 * True when an edit leaves the value as entered: numbers compare by value, anything else (an empty cell included)
 * by its trimmed text, so clearing a zero counts as a change.
 */
export const isSameEnteredValue = (before: unknown, after: unknown): boolean => {
  const [was, now] = [before, after].map(value => String(value ?? '').trim());
  const [wasNumber, nowNumber] = [was, now].map(text => (text === '' ? NaN : Number(text)));
  return Number.isFinite(wasNumber) && Number.isFinite(nowNumber) ? wasNumber === nowNumber : was === now;
};

/**
 * Takes the cell's editable Excel references off and remembers the saved ones, so the next Save retires them;
 * AI proposals stay for review. True when anything was taken off.
 */
export const stripEditableMappings = (cell: ReferenceCell): boolean => {
  const [removed, kept] = partition(cell.mappingReferences ?? [], isEditableMappingReference);
  if (removed.length === 0) {
    return false;
  }
  const savedIds = removed.flatMap(ref => (ref.id != null ? [ref.id] : []));
  cell.mappingReferences = kept;
  cell.retiredMappingIds = [...new Set([...(cell.retiredMappingIds ?? []), ...savedIds])];
  return true;
};

/** The security fields Save reads for references; every other field passes through unchanged. */
type SaveSecurity = {
  id?: number | string | null;
  citation_references?: unknown;
  // The generated security type calls these strings; at runtime they are the cells' Excel reference rows.
  mapping_references?: unknown;
  retire_mapping_ids?: number[] | null;
};

/**
 * What a security's Save sends about references: a new security (a cloned column included) carries only its
 * accepted citations and editable Excel references, so carried AI proposals stay in review; a saved one sends
 * only the Excel references a manual edit removed.
 */
export const withSaveReferences = <T extends SaveSecurity>(security: T) => {
  const {
    citation_references: citations,
    mapping_references: mappings,
    retire_mapping_ids: retireIds,
    ...rest
  } = security;
  if (!isSavedSecurityId(security.id)) {
    return {
      ...rest,
      citation_references: convertCamelCaseToSnakeCase(
        (citations as CitationReferenceData[] | undefined)?.filter(ref => citationReviewStatus(ref) === 'accepted')
      ),
      mapping_references: (mappings as MappingFlagged[] | undefined)?.filter(isEditableMappingReference),
    };
  }
  return retireIds?.length ? { ...rest, retire_mapping_ids: retireIds } : rest;
};
